import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TwoFactorApiService, TwoFactorMethod } from 'shared/services/api/two-factor.api.service';
import { delay, take } from 'rxjs';
import { SnackbarService } from 'shared/services/snackbar.service';

export interface TwoFactorDialogData {
    tempToken: string;
    method: TwoFactorMethod;
    maskedEmail?: string;
    deviceInfo?: {
        browser?: string;
        deviceName?: string;
        platform?: string;
    };
}

export interface TwoFactorDialogResult {
    success: boolean;
    data?: any;
    usedRecoveryKey?: boolean;
}

@Component({
    standalone: true,
    selector: 'two-factor-dialog',
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule
    ],
    templateUrl: './two-factor-dialog.component.html'
})
export class TwoFactorDialogComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef<HTMLInputElement>>;

    code = '';
    recoveryKey = '';
    useRecoveryKey = false;
    isProcessing = false;
    isResending = false;
    error: string | null = null;
    filledInputs: boolean[] = [false, false, false, false, false, false];
    
    resendTimer = 60;
    private _resendInterval: any;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: TwoFactorDialogData,
        private _dialogRef: MatDialogRef<TwoFactorDialogComponent, TwoFactorDialogResult>,
        private _twoFactorApi: TwoFactorApiService,
        private _snackbarService: SnackbarService
    ) {}

    ngOnInit(): void {
        // Uruchom timer dla email
        if (this.data.method === 'email') {
            this.startResendTimer();
        }
    }

    ngAfterViewInit(): void {
        // Poczekaj aż dialog się w pełni otworzy (animacja Material) i inputy będą w DOM
        this._dialogRef.afterOpened()
            .pipe(take(1))
            .subscribe(() => {
                this._focusFirstInput();
            });

        // Fallback: jeśli ViewChildren jeszcze nie były gotowe, nasłuchuj zmian
        this.codeInputs.changes
            .pipe(take(1))
            .subscribe(() => {
                this._focusFirstInput();
            });
    }

    private _focusFirstInput(): void {
        setTimeout(() => {
            const inputs = this.codeInputs?.toArray();
            if (inputs?.length) {
                inputs[0].nativeElement.focus();
            }
        }, 50);
    }

    ngOnDestroy(): void {
        if (this._resendInterval) {
            clearInterval(this._resendInterval);
        }
    }

    private startResendTimer(): void {
        this.resendTimer = 60;
        this._resendInterval = setInterval(() => {
            this.resendTimer--;
            if (this.resendTimer <= 0) {
                clearInterval(this._resendInterval);
            }
        }, 1000);
    }

    /**
     * Przełącza między trybem kodu a kluczem odzyskiwania
     */
    toggleMode(): void {
        this.useRecoveryKey = !this.useRecoveryKey;
        this.error = null;

        if (!this.useRecoveryKey) {
            // Reset stanu kodu (inputy jeszcze nie istnieją w DOM)
            this.code = '';
            this.filledInputs = [false, false, false, false, false, false];

            // Poczekaj aż Angular wyrenderuje inputy, potem wyczyść i focusuj
            setTimeout(() => {
                const inputs = this.codeInputs?.toArray();
                if (inputs?.length) {
                    inputs.forEach(input => input.nativeElement.value = '');
                    inputs[0].nativeElement.focus();
                }
            }, 50);
        }
    }

    /**
     * Czyści błąd przy focusie na input
     */
    onInputFocus(): void {
        if (this.error) {
            this.error = null;
        }
    }

    onCodeInput(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;

        // Aktualizuj kod i stan wypełnienia
        const codeArray = this.code.split('');
        codeArray[index] = value;
        this.code = codeArray.join('').substring(0, 6);
        this.filledInputs[index] = !!value;
        this.error = null;

        // Przejdź do następnego pola
        if (value && index < 5) {
            const inputs = this.codeInputs.toArray();
            inputs[index + 1].nativeElement.focus();
        }

        // Auto-submit gdy wprowadzono 6 cyfr
        if (this.code.length === 6) {
            this.verify();
        }
    }

    onCodeKeydown(event: KeyboardEvent, index: number): void {
        const input = event.target as HTMLInputElement;

        if (event.key === 'Backspace' && !input.value && index > 0) {
            const inputs = this.codeInputs.toArray();
            inputs[index - 1].nativeElement.focus();
        }
    }

    onCodePaste(event: ClipboardEvent): void {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData('text') || '';
        const cleanedCode = pastedData.replace(/\D/g, '').substring(0, 6);

        if (cleanedCode.length === 6) {
            this.code = cleanedCode;
            const inputs = this.codeInputs.toArray();
            
            for (let i = 0; i < 6; i++) {
                inputs[i].nativeElement.value = cleanedCode[i] || '';
                this.filledInputs[i] = true;
            }
            
            inputs[5].nativeElement.focus();
            
            // Auto-submit
            this.verify();
        }
    }

    verify(): void {
        if (this.code.length !== 6 || this.isProcessing) return;

        this.isProcessing = true;
        this.error = null;

        this._twoFactorApi.verifyLogin(this.data.tempToken, this.code, this.data.deviceInfo)
            .pipe(delay(1000), take(1))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this._dialogRef.close({
                            success: true,
                            data: response.data,
                            usedRecoveryKey: response.data?.usedRecoveryKey
                        });
                    } else {
                        this.error = response.message || 'Nieprawidłowy kod';
                        this.clearCode();
                    }
                    this.isProcessing = false;
                },
                error: (err) => {
                    this.error = err.error?.message || 'Nieprawidłowy kod weryfikacyjny';
                    this.clearCode();
                    this.isProcessing = false;
                }
            });
    }

    verifyRecoveryKey(): void {
        if (!this.recoveryKey || this.isProcessing) return;

        this.isProcessing = true;
        this.error = null;

        this._twoFactorApi.verifyLogin(this.data.tempToken, this.recoveryKey, this.data.deviceInfo)
            .pipe(delay(1000), take(1))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this._snackbarService.showSnackBar(
                            'Zalogowano przy użyciu klucza odzyskiwania. Rozważ wygenerowanie nowych kluczy.',
                            'warning'
                        );
                        this._dialogRef.close({
                            success: true,
                            data: response.data,
                            usedRecoveryKey: true
                        });
                    } else {
                        this.error = response.message || 'Nieprawidłowy klucz odzyskiwania';
                    }
                    this.isProcessing = false;
                },
                error: (err) => {
                    this.error = err.error?.message || 'Nieprawidłowy klucz odzyskiwania';
                    this.isProcessing = false;
                }
            });
    }

    resendCode(): void {
        if (this.resendTimer > 0 || this.isResending) return;

        this.isResending = true;

        this._twoFactorApi.resendLoginEmail(this.data.tempToken)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this._snackbarService.showSnackBar('Nowy kod został wysłany', 'success');
                        this.startResendTimer();
                    } else {
                        this._snackbarService.showSnackBar(response.message || 'Nie udało się wysłać kodu', 'error');
                    }
                    this.isResending = false;
                },
                error: (err) => {
                    this._snackbarService.showSnackBar(err.error?.message || 'Nie udało się wysłać kodu', 'error');
                    this.isResending = false;
                }
            });
    }

    private clearCode(): void {
        this.code = '';
        this.filledInputs = [false, false, false, false, false, false];
        const inputs = this.codeInputs?.toArray();
        if (inputs?.length) {
            inputs.forEach(input => input.nativeElement.value = '');
            inputs[0].nativeElement.focus();
        }
    }
}
